import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "@/lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle("lenis-stopped", open);
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
          scrolled ? "border-line bg-ink/90 backdrop-blur-md" : "border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8 lg:px-16">
          <Link to="/" data-testid="nav-home-link" className="flex items-center gap-3">
            <img src="/logo-white.png" alt="Black Sky Builders" className="h-12 w-auto" />
          </Link>

          <nav className="hidden items-center gap-6 xl:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-white" : "text-fog hover:text-silver"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={PHONE_TEL}
              data-testid="nav-phone-link"
              className="hidden items-center gap-2 whitespace-nowrap font-mono text-[11px] tracking-[0.15em] text-silver transition-colors hover:text-white md:flex"
            >
              <Phone className="h-3.5 w-3.5" /> {PHONE_DISPLAY}
            </a>
            <Link to="/contact" data-testid="nav-cta-consultation-button" className="btn-primary hidden !px-5 !py-3 sm:inline-flex">
              Request Consultation
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className="flex h-11 w-11 items-center justify-center border border-line text-white transition-colors hover:border-silver xl:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col bg-ink pt-24 xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="blueprint-grid pointer-events-none absolute inset-0 opacity-30" />
            <nav className="relative flex flex-1 flex-col gap-1 overflow-y-auto px-6 pb-8">
              {[{ to: "/", label: "Home", testid: "nav-home-link-mobile" }, ...NAV_LINKS.map((l) => ({ ...l, testid: `${l.testid}-mobile` }))].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={l.testid}
                    className={({ isActive }) =>
                      `block border-b border-line py-4 font-display text-2xl font-black uppercase tracking-tight ${
                        isActive ? "text-white" : "text-fog"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
                className="mt-8 flex flex-col gap-3"
              >
                <Link to="/contact" data-testid="mobile-menu-consult-button" className="btn-primary">
                  Request a Project Consultation
                </Link>
                <a href={PHONE_TEL} data-testid="mobile-menu-call-button" className="btn-outline">
                  <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
